import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { X, Info, ArrowRight } from "@phosphor-icons/react";
import { IS_FARMERSPULSE } from "@/lib/site";
import ListenTranslateBar from "@/components/ListenTranslateBar";

/**
 * Small ⓘ chip that opens a right-side slide-over drawer with a guide
 * article's contents. Prefetches the guide on mount so the drawer opens
 * instantly. Falls back gracefully when the guide isn't published.
 *
 * The drawer is portalled to `document.body` so it never inherits
 * typography/tracking/casing from parent module cards.
 *
 * <GuideHelperChip slug="weather-today" label="How this works" />
 */
export default function GuideHelperChip({ slug, label = "How this works" }) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    http.get(`/cms/pages/by-slug/guide/${slug}`)
      .then(({ data }) => { if (!cancelled) setPage(data.page); })
      .catch(() => { if (!cancelled) setErr(true); });
    return () => { cancelled = true; };
  }, [slug]);

  // Close on Escape while the drawer is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const guideHref = IS_FARMERSPULSE ? `/guides/${slug}` : `/farmers/guides/${slug}`;

  const drawer = open ? (
    <div
      className="fixed inset-0 z-[85] font-sans normal-case tracking-normal text-base"
      style={{ letterSpacing: "normal", textTransform: "none" }}
      data-testid={`guide-drawer-${slug}`}
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={() => setOpen(false)}
      />
      <aside
        className="absolute right-0 top-0 h-full w-full sm:w-[520px] bg-[#F5EBDC] shadow-2xl border-l border-black/10 overflow-y-auto animate-in slide-in-from-right duration-200 font-sans normal-case"
        style={{ letterSpacing: "normal", textTransform: "none" }}
      >
        <header
          className="sticky top-0 z-10 flex items-center justify-between gap-3 px-5 py-4 bg-[#F5EBDC]/95 backdrop-blur border-b border-black/10"
        >
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#2D8659]">
            Feature guide
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {page && (
              <ListenTranslateBar
                text={(page.body_html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()}
                testidPrefix={`guide-drawer-tools-${slug}`}
              />
            )}
            <button
              onClick={() => setOpen(false)}
              data-testid={`guide-drawer-close-${slug}`}
              className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-white border border-black/15 hover:border-black/30"
              aria-label="Close"
            >
              <X size={14} weight="bold" />
            </button>
          </div>
        </header>

        <div className="px-5 py-6 text-[#241209]">
          {err ? (
            <div className="text-base">Guide unavailable.</div>
          ) : !page ? (
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#8E8B7F]">Loading…</div>
          ) : (
            <>
              <h2
                className="font-serif-display text-3xl leading-tight tracking-tight text-[#151312] normal-case"
                style={{ letterSpacing: "normal", textTransform: "none" }}
              >
                {page.title}
              </h2>
              {page.subtitle && (
                <div className="mt-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#8E8B7F]">
                  {page.subtitle}
                </div>
              )}
              <article
                className="mt-5 text-base leading-relaxed text-[#241209] normal-case [&_h2]:font-serif-display [&_h2]:text-xl [&_h2]:text-[#151312] [&_h2]:mt-6 [&_h2]:mb-2 [&_h2]:normal-case [&_h2]:tracking-normal [&_p]:mt-3 [&_p]:normal-case [&_p]:tracking-normal [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-3 [&_ul]:space-y-1 [&_li]:normal-case [&_li]:tracking-normal [&_strong]:text-[#151312]"
                style={{ letterSpacing: "normal", textTransform: "none" }}
                dangerouslySetInnerHTML={{ __html: page.body_html || "" }}
              />
              <Link
                to={guideHref}
                onClick={() => setOpen(false)}
                data-testid={`guide-drawer-full-${slug}`}
                className="mt-8 inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-[#2D8659]"
              >
                Open full guide <ArrowRight size={11} weight="bold" />
              </Link>
            </>
          )}
        </div>
      </aside>
    </div>
  ) : null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`guide-chip-${slug}`}
        className="ml-2 inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#3A3830] hover:border-[color:var(--np-brand,#2D8659)] hover:text-[color:var(--np-brand,#2D8659)] transition-colors"
        aria-label={`${label}: ${slug}`}
      >
        <Info size={11} weight="fill" /> {label}
      </button>
      {typeof document !== "undefined" && drawer && createPortal(drawer, document.body)}
    </>
  );
}
